import { BigNum } from '@velocity-exchange/sdk';

/**
 * The collateral action gate — a deep module for "can I submit this
 * collateral action?".
 *
 * The collateral modals (deposit / withdraw / borrow / transfer / repay /
 * withdraw-to-other-destinations) each used to re-derive this decision as an
 * ad-hoc boolean. This hook is the single seam for it: the common clauses
 * (submitting guard, wallet connection, empty/zero amount, ceiling
 * comparison, and the ceiling's load state) live behind this interface, and
 * per-modal clauses compose through `extraChecks` without this module
 * needing to know about them.
 *
 * Loading is NOT validation-passed (see .claude/docs/failure-modes.md):
 * while `maxAmountStatus` is `'loading'` or `'error'` the gate always
 * blocks, and `exceededMax` is only reported once the ceiling is loaded.
 *
 * The hook is a pure derivation (no React state or effects), so it can be
 * exercised directly in unit tests without mounting anything.
 */

export type CollateralActionGateStatus = 'loading' | 'loaded' | 'error';

/** Block reasons emitted by the gate's own clauses, in evaluation order. */
export type CollateralActionGateCommonReason =
	| 'WALLET_NOT_CONNECTED'
	| 'SUBMITTING'
	| 'MAX_LOADING'
	| 'MAX_ERROR'
	| 'AMOUNT_EMPTY'
	| 'AMOUNT_NOT_POSITIVE'
	| 'EXCEEDED_MAX';

export interface CollateralActionGateCheck {
	/** Stable identifier — becomes `blockingReason` when this check blocks. */
	id: string;
	/** True when this clause should block submission. */
	blocked: boolean;
}

export interface CollateralActionGateArgs {
	/** Parsed action amount. Must be > 0 to submit. */
	amount: BigNum;
	/**
	 * Raw input string. Provide it to also block on an empty input
	 * (`AMOUNT_EMPTY`). Omit for callers that only track a parsed amount.
	 */
	amountInput?: string;
	/**
	 * The action ceiling (max deposit, withdrawal limit, …). When provided,
	 * `amount > maxAmount` blocks with `EXCEEDED_MAX`. Omit when the modal
	 * has no ceiling clause.
	 */
	maxAmount?: BigNum;
	/**
	 * Load state of `maxAmount`. `'loading'` and `'error'` always block.
	 * Defaults to `'loaded'` for callers whose ceiling is derived
	 * synchronously from store state.
	 */
	maxAmountStatus?: CollateralActionGateStatus;
	/** In-flight submission guard. */
	isSubmitting: boolean;
	/**
	 * Wallet connection state. Omit when the modal does not gate on
	 * connection (matches historical per-modal behaviour).
	 */
	isWalletConnected?: boolean;
	/**
	 * Per-modal clauses, evaluated in order after the common clauses.
	 * The first blocked check's `id` becomes `blockingReason`.
	 */
	extraChecks?: readonly CollateralActionGateCheck[];
}

export interface CollateralActionGateResult {
	/** All clauses pass — safe to enable the submit button. */
	canSubmit: boolean;
	/** `id` of the first blocking clause, or null when `canSubmit`. */
	blockingReason:
		| CollateralActionGateCommonReason
		// Keeps extension-check ids assignable while preserving autocomplete
		// for the common reasons.
		| (string & NonNullable<unknown>)
		| null;
	/**
	 * `amount > maxAmount` with the ceiling fully loaded. Exposed for input
	 * clamping and banner selection; always false while the ceiling is
	 * loading or errored, and when no ceiling was provided.
	 */
	exceededMax: boolean;
	/** Convenience: `maxAmountStatus === 'loading'`. */
	isMaxLoading: boolean;
}

export function useCollateralActionGate({
	amount,
	amountInput,
	maxAmount,
	maxAmountStatus = 'loaded',
	isSubmitting,
	isWalletConnected,
	extraChecks,
}: CollateralActionGateArgs): CollateralActionGateResult {
	const isMaxLoading = maxAmount !== undefined && maxAmountStatus === 'loading';
	const exceededMax =
		maxAmount !== undefined &&
		maxAmountStatus === 'loaded' &&
		amount.gt(maxAmount);

	const blockingReason = ((): CollateralActionGateResult['blockingReason'] => {
		if (isWalletConnected === false) return 'WALLET_NOT_CONNECTED';
		if (isSubmitting) return 'SUBMITTING';
		if (isMaxLoading) return 'MAX_LOADING';
		if (maxAmount !== undefined && maxAmountStatus === 'error')
			return 'MAX_ERROR';
		if (amountInput !== undefined && !amountInput) return 'AMOUNT_EMPTY';
		if (!amount.gtZero()) return 'AMOUNT_NOT_POSITIVE';
		if (exceededMax) return 'EXCEEDED_MAX';

		const blockedCheck = extraChecks?.find((check) => check.blocked);
		if (blockedCheck) return blockedCheck.id;

		return null;
	})();

	return {
		canSubmit: blockingReason === null,
		blockingReason,
		exceededMax,
		isMaxLoading,
	};
}
